import {
  ApplicationRef, Component, ElementRef, Input, Output, EventEmitter, ChangeDetectionStrategy, HostListener, OnInit,
  OnDestroy, afterNextRender, inject, viewChild,
} from '@angular/core'
import { ActivityTrackerService, type ActivityModalHandle } from '../../../core/activity/activity-tracker.service'
import type { ActivityModalName } from '../../../core/activity/activity.model'
import { canTakeFocus, focusIsLost, focusedElement, refocus, tabbablesIn } from '../../utils/focus'

let nextModalShellId = 0

/** The open shells, in the order they opened. */
const openShells = new Set<ModalShellComponent>()

/** A closed shell's focus return, waiting for the render that removes it (see `ngOnDestroy`). */
interface PendingRestore {
  readonly opener: HTMLElement | null
  cancelled: boolean
}
const pendingRestores: PendingRestore[] = []

/**
 * The open shell that paints on top: the one whose panel comes last in the
 * document (every shell has the same `--z-modal`), not the one opened last.
 * A shell whose panel is not rendered yet counts only when it is the newest
 * (it is about to appear); the others are compared by their rendered panels.
 */
export function topmostModalShell(): ModalShellComponent | null {
  const shells = [...openShells]
  const newest = shells[shells.length - 1]
  if (!newest) return null
  if (!newest.renderedPanel()) return newest
  let top: ModalShellComponent | null = null
  let topPanel: HTMLElement | null = null
  for (const shell of shells) {
    const panel = shell.renderedPanel()
    if (!panel) continue
    if (!topPanel || topPanel.compareDocumentPosition(panel) & Node.DOCUMENT_POSITION_FOLLOWING) {
      top = shell
      topPanel = panel
    }
  }
  return top
}

/**
 * The topmost dialog shell when it is not one of the screen's under `host`,
 * else null: its panel is rendered outside `host`, or — not rendered yet, so
 * about to appear — while the screen has no dialog of its own open
 * (`screenHasDialogs`; a screen dialog counts from the moment its flag is
 * set). A screen shell still in the page for a render after its flag went
 * (Keep editing, then a key) is the screen's: it blocks nothing, and its own
 * Escape handler answers. Screens use it to leave Escape and their keys to an
 * app-level dialog (What's New, the update modal, the basemap key dialog).
 */
export function appDialogOnTop(host: Element, screenHasDialogs: boolean): ModalShellComponent | null {
  const top = topmostModalShell()
  if (!top) return null
  const panel = top.renderedPanel()
  if (!panel) return screenHasDialogs ? null : top
  return host.contains(panel) ? null : top
}

/**
 * The one Tab trap, for the topmost shell only. On `window` in the capture
 * phase: it runs before the editors' document capture guard
 * (`modalKeyGuard`), which stops every non-form key. It never stops the
 * event itself, so that guard and the activity tracker still see it.
 */
const onWindowKeydown = (e: KeyboardEvent): void => topmostModalShell()?.trapTab(e)

/**
 * Where focus goes once a closed shell is gone. With another shell still
 * open, focus belongs in the topmost one: its own control the closed dialog
 * was opened from, else its initial target — unless a screen already put
 * focus inside an open shell. With none, the opener takes back focus that is
 * lost (on `body`), if it still can; focus a screen placed itself stays.
 */
function restoreAfterClose(opener: HTMLElement | null): void {
  const top = topmostModalShell()
  if (!top) {
    if (focusIsLost() && canTakeFocus(opener)) refocus(opener)
    return
  }
  const panel = top.renderedPanel()
  if (!panel) return
  const active = document.activeElement
  if (!focusIsLost() && [...openShells].some(s => s.renderedPanel()?.contains(active))) return
  if (canTakeFocus(opener) && panel.contains(opener) && refocus(opener)) return
  top.focusInitial()
}

/**
 * The control a dialog marked for initial focus (`data-modal-initial-focus`):
 * the marked element when it is a control, else the first Tab stop inside it
 * (`<app-form-field data-modal-initial-focus>` marks its input).
 */
function markedControl(panel: HTMLElement): HTMLElement | null {
  const marked = panel.querySelector<HTMLElement>('[data-modal-initial-focus]')
  if (!marked) return null
  return tabbablesIn(marked)[0] ?? (canTakeFocus(marked) ? marked : null)
}

/**
 * ModalShellComponent — reusable modal backdrop + panel wrapper.
 *
 * Provides the `.modal-backdrop` / `.modal-panel` structure so individual
 * dialogs only need to supply their inner content.
 *
 * Usage:
 *   <app-modal-shell title="Confirm Action" (closed)="onClose()">
 *     <p>Are you sure?</p>
 *     <div actions>
 *       <button class="btn btn-secondary" (click)="onClose()">Cancel</button>
 *       <button class="btn btn-primary"   (click)="onConfirm()">OK</button>
 *     </div>
 *   </app-modal-shell>
 *
 * The `[actions]` attribute selector projects content into the actions slot,
 * which is rendered below a top border inside the panel.
 *
 * Inputs:
 *   title          — heading text (required by convention; pass '' to omit)
 *   variant        — 'default' renders the title in dark ink;
 *                    'error' renders it in red (var(--color-danger))
 *   size           — controls max-width: sm=360px, md=480px (default), lg=600px
 *   closeOnBackdrop — when true, clicking the backdrop emits `closed`
 *   activityName   — Editor Activity Tracking: the modal's name for `modal.open` /
 *                    `modal.close` (default 'other'; only Editor screens are tracked)
 *
 * Output:
 *   closed         — emitted when the backdrop is clicked (if closeOnBackdrop)
 *                    or whenever the host wants to signal dismissal (e.g.
 *                    close buttons inside the projected content call it too)
 *
 * Focus (backlog 16). Every shell is created and destroyed by an `@if`, so it
 * exists exactly while its dialog is open.
 * - Opening records the focused element (the opener) and, after the render,
 *   moves focus to the control the dialog marked `data-modal-initial-focus`
 *   (a text field: Add User's First Name, the basemap key input), else to
 *   `.modal-content` (`tabindex="-1"`: arrow keys and Page Down scroll it; a
 *   held Enter or Space cannot press a dialog button) — unless the dialog
 *   focused a control itself (the viewer's Close) or another open shell sits
 *   on top of this one. A marked control that is disabled or gone falls back
 *   to the content area. Not `autofocus`: browsers act on it at page load
 *   only, and there on their own, ignoring which dialog is on top.
 * - Tab and Shift+Tab stay inside the topmost shell (one window capture
 *   listener for all of them). `.modal-content` is a Tab stop of its own
 *   while it overflows, so a long dialog stays scrollable by keyboard. Only
 *   Tab is trapped, never `focusin`: a screen may move focus out on purpose
 *   while its dialog is still in the page (the unapplied-changes prompt's
 *   Apply runs the action from its click handler).
 * - Closing returns focus to the opener after the render that removed the
 *   dialog, and only when focus is lost by then (on `body`): a screen's own
 *   focus placement for the same answer (the work pane's heading, row or
 *   panel) runs first and wins. A dialog opened in the same pass as another
 *   one closes takes over that one's opener; a dialog closing over another
 *   open one returns focus into it. One accepted edge: the Inspection Group
 *   review blurs its buttons whenever the group changes, but a group change
 *   while its tutorial is open leaves the opener (an answer button reached by
 *   Tab) in place, so closing the tutorial focuses that button for the new
 *   group.
 */
@Component({
  selector: 'app-modal-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="modal-backdrop"
         (click)="onBackdropClick()">
      <div #panel
           class="modal-panel"
           tabindex="-1"
           role="dialog"
           aria-modal="true"
           [attr.aria-labelledby]="title ? titleId : null"
           [class.modal-panel--sm]="size === 'sm'"
           [class.modal-panel--lg]="size === 'lg'"
           (click)="$event.stopPropagation()">
        @if (title) {
          <h2 class="modal-title"
              [id]="titleId"
              [class.modal-title--error]="variant === 'error'">{{ title }}</h2>
        }

        <div #content class="modal-content" tabindex="-1">
          <ng-content />
        </div>

        <div class="modal-actions">
          <ng-content select="[actions]" />
        </div>
      </div>
    </div>
  `,
  styles: [`
    /*
     * The global styles.scss already defines the base .modal-backdrop,
     * .modal-panel, .modal-title, .modal-actions, .btn, etc.  These host
     * styles only add the size modifiers and the error-title variant that
     * are specific to this component.
     */
    .modal-panel--sm { max-width: 360px; }
    .modal-panel--lg { max-width: 600px; }
    .modal-title--error { color: var(--color-danger); }

    .modal-panel {
      display: flex;
      flex-direction: column;
      max-height: calc(100dvh - 64px);
    }

    .modal-content {
      overflow-y: auto;
      flex: 1;
      min-height: 0;
    }

    /* Focus targets, not controls (see the focus notes above): no ring on the
       panel; a keyboard-focused content area shows the work pane's panel ring. */
    .modal-panel:focus { outline: none; }
    .modal-content:focus { outline: none; }
    .modal-content:focus-visible { outline: 2px solid var(--color-primary); outline-offset: -2px; }

    /* Suppress the actions row when the slot has no projected nodes */
    .modal-actions:empty { display: none; }
  `]
})
export class ModalShellComponent implements OnInit, OnDestroy {
  private readonly activity = inject(ActivityTrackerService)
  /** Outlives this shell: its focus return runs after the shell is destroyed. */
  private readonly appRef = inject(ApplicationRef)
  private readonly panelRef = viewChild<ElementRef<HTMLElement>>('panel')
  private readonly contentRef = viewChild<ElementRef<HTMLElement>>('content')
  private activityHandle: ActivityModalHandle | null = null
  /** The element focused when the dialog opened (or handed over, see the constructor); null for `body`. */
  private opener: HTMLElement | null
  @Input() title = ''
  @Input() variant: 'default' | 'error' = 'default'
  @Input() size: 'sm' | 'md' | 'lg' = 'md'
  @Input() closeOnBackdrop = false
  @Input() activityName: ActivityModalName = 'other'
  @Output() closed = new EventEmitter<void>()

  readonly titleId = `modal-shell-title-${nextModalShellId++}`

  constructor() {
    // Captured now, while the parent creates the dialog: by the first render a dialog may have focused itself.
    this.opener = focusedElement() ?? this.takePendingOpener()
    openShells.add(this)
    if (openShells.size === 1) window.addEventListener('keydown', onWindowKeydown, true)
    // The `read` phase runs after every other hook's default phase in the same render, so a dialog's own
    // after-render focus (the basemap key input) lands first, whatever view registered it (see ngOnDestroy).
    afterNextRender({
      read: () => {
        if (topmostModalShell() === this) this.focusInitial()
      },
    })
  }

  ngOnInit(): void {
    this.activityHandle = this.activity.modalOpened(this.activityName)
  }

  ngOnDestroy(): void {
    this.activity.modalClosed(this.activityHandle)
    openShells.delete(this)
    if (openShells.size === 0) window.removeEventListener('keydown', onWindowKeydown, true)
    // A dialog opened from this one (its opener is one of this dialog's controls) returns where this one would.
    const panel = this.panelRef()?.nativeElement
    if (panel) {
      for (const shell of openShells) {
        if (shell.opener && panel.contains(shell.opener)) shell.opener = this.opener
      }
    }
    // Deferred: the view's DOM is already detached (focus inside it is on `body`), and the screen's own
    // focus work for the answer that closed the dialog must land first. That work is an after-render hook
    // on the screen's view, which Angular only queues once that view's refresh ends — after this destroy,
    // and so after a default-phase hook registered here. The `read` phase runs after every default-phase
    // hook of the same render, so the screen's placement always wins.
    const pending: PendingRestore = { opener: this.opener, cancelled: false }
    pendingRestores.push(pending)
    afterNextRender({
      read: () => {
        const at = pendingRestores.indexOf(pending)
        if (at !== -1) pendingRestores.splice(at, 1)
        if (!pending.cancelled) restoreAfterClose(pending.opener)
      },
    }, { injector: this.appRef.injector })
  }

  /** The panel, once it is in the page. */
  renderedPanel(): HTMLElement | null {
    const panel = this.panelRef()?.nativeElement
    return panel?.isConnected ? panel : null
  }

  /**
   * Moves focus to the dialog's initial target (its marked control, else
   * `.modal-content`, else the panel) unless it is already inside.
   */
  focusInitial(): void {
    const panel = this.renderedPanel()
    if (!panel || panel.contains(document.activeElement)) return
    if (refocus(markedControl(panel))) return
    ;(this.contentRef()?.nativeElement ?? panel).focus({ preventScroll: true })
  }

  /** Keeps Tab and Shift+Tab inside this (the topmost) dialog; see the focus notes above. */
  trapTab(e: KeyboardEvent): void {
    if (e.key !== 'Tab' || e.defaultPrevented || e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return
    const panel = this.renderedPanel()
    if (!panel) return
    const scroller = this.overflowingContent()
    const items = tabbablesIn(panel)
    if (scroller) {
      // In document order: before its own controls (they follow it) and the actions row.
      const at = items.findIndex(el => !!(scroller.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING))
      items.splice(at === -1 ? items.length : at, 0, scroller)
    }
    if (items.length === 0) {
      e.preventDefault()
      panel.focus({ preventScroll: true })
      return
    }
    const target = this.tabTarget(items, panel, scroller, !e.shiftKey)
    if (!target) return
    e.preventDefault()
    target.focus()
  }

  /**
   * Where Tab goes when the browser cannot be left to it, or null when it can:
   * a step between two controls inside the dialog is the browser's own (radio
   * groups, iframes and the like keep their native behaviour). The trap moves
   * focus itself at either end (wrapping round), into or out of the
   * scrollable content stop (its `tabindex="-1"` is no Tab stop to the
   * browser), and from anywhere else — the panel, the content area, `body`
   * after a backdrop click, or outside the dialog.
   */
  private tabTarget(items: HTMLElement[], panel: HTMLElement, scroller: HTMLElement | null, forward: boolean): HTMLElement | null {
    const last = items.length - 1
    const wrapTo = forward ? items[0] : items[last]
    const active = document.activeElement
    const i = active instanceof HTMLElement ? items.indexOf(active) : -1
    if (i !== -1) {
      const j = forward ? i + 1 : i - 1
      if (j < 0 || j > last) return wrapTo
      return active === scroller || items[j] === scroller ? items[j] : null
    }
    if (!active || active === panel || !panel.contains(active)) return wrapTo
    // Focus on something inside the dialog that is no Tab stop (the content area): the next stop in document order.
    if (forward) {
      return items.find(el => !!(active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) ?? wrapTo
    }
    for (let k = last; k >= 0; k--) {
      if (active.compareDocumentPosition(items[k]) & Node.DOCUMENT_POSITION_PRECEDING) return items[k]
    }
    return wrapTo
  }

  /** `.modal-content` while it overflows: Chromium makes such a scroller a Tab stop, so the trap keeps it one. */
  private overflowingContent(): HTMLElement | null {
    const content = this.contentRef()?.nativeElement
    return content && content.scrollHeight > content.clientHeight ? content : null
  }

  /**
   * A dialog opened with nothing focused, in the same pass as another one
   * closed (an answer that runs an action opening the next dialog): it takes
   * over the closed one's opener, whose own return is then dropped — no
   * stray focus on the background control in between. Only the newest
   * closed dialog counts: when it had no opener (or its opener left the
   * page), an older one's is not taken.
   */
  private takePendingOpener(): HTMLElement | null {
    let newest: PendingRestore | null = null
    for (let k = pendingRestores.length - 1; k >= 0 && !newest; k--) {
      if (!pendingRestores[k].cancelled) newest = pendingRestores[k]
    }
    const opener = newest?.opener?.isConnected ? newest.opener : null
    // An entry whose opener left the page can hand nothing over: drop it (its own return still runs).
    for (let k = pendingRestores.length - 1; k >= 0; k--) {
      const gone = pendingRestores[k].opener
      if (gone && !gone.isConnected) pendingRestores.splice(k, 1)
    }
    if (!newest || !opener) return null
    newest.cancelled = true
    return opener
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop) {
      this.closed.emit()
    }
  }

  /**
   * Escape's answer: emits `closed`, as a Close button does. A dialog that
   * binds no `(closed)` (the update modal) stays.
   */
  dismiss(): void {
    this.closed.emit()
  }

  /**
   * Escape closes the topmost dialog only: every open shell hears it on
   * `document`, and `stopPropagation` does not stop the others' listeners (a
   * What's New under the update modal would close). On editor screens the
   * editors' `modalKeyGuard` stops Escape before this runs: it answers from
   * the screen's own modal list while the topmost shell is one of the
   * screen's, else it calls the topmost shell's `dismiss()` (backlog 19).
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (topmostModalShell() !== this) return
    event.stopPropagation()
    this.dismiss()
  }
}
